import React, { useState, useEffect } from 'react';
import { getUrl } from 'aws-amplify/storage';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Container,
  Header,
  Box,
  SpaceBetween,
  Button,
  Tabs,
  FormField,
  Input,
  Textarea
} from '@cloudscape-design/components';
import ChatInterface from './ChatInterface';

const RecordingDetails = ({ video, onBack, backButtonText = 'Back' }) => {
  const [videoUrl, setVideoUrl] = useState('');
  const [transcript, setTranscript] = useState('');
  const [summary, setSummary] = useState('');
  const [activeTab, setActiveTab] = useState('summary');
  const [transcriptSearchText, setTranscriptSearchText] = useState('');
  const [transcriptHighlights, setTranscriptHighlights] = useState([]);

  useEffect(() => {
    if (video) {
      loadSummary(video);
    }
  }, [video]);

  // Search transcript when search text changes
  useEffect(() => {
    if (transcriptSearchText && transcript) {
      const regex = new RegExp(transcriptSearchText, 'gi');
      const matches = [...transcript.matchAll(regex)];
      setTranscriptHighlights(matches.map(match => ({
        index: match.index,
        length: transcriptSearchText.length
      })));
    } else {
      setTranscriptHighlights([]);
    }
  }, [transcriptSearchText, transcript]);

  // Load video lazily when tab is selected
  const loadVideo = async (videoItem) => {
    if (videoUrl) return; // Already loaded
    
    try {
      console.log("Loading video with key:", videoItem.key);
      
      const url = await getUrl({ 
        path: videoItem.key,
        options: { 
          expiresIn: 3600,
          validateObjectExistence: true
        }
      });
      
      console.log("Video URL generated:", url.url.toString());
      setVideoUrl(url.url.toString());
    } catch (err) {
      console.error('Error loading video:', err);
    }
  };

  // Load transcript lazily when tab is selected
  const loadTranscript = async (videoItem) => {
    if (transcript) return; // Already loaded
    
    try {
      const transcriptKey = videoItem.key.replace(/\.webm$/i, '.json');
      console.log("Looking for transcript with key:", transcriptKey);
      
      const transcriptUrl = await getUrl({ 
        path: transcriptKey, 
        options: { 
          expiresIn: 3600,
          validateObjectExistence: true
        }
      });
      
      const response = await fetch(transcriptUrl.url.toString());
      if (response.ok) {
        const transcriptText = await response.text();
        try {
          const parsedJson = JSON.parse(transcriptText);
          const extractedTranscript = parsedJson?.results?.transcripts?.[0]?.transcript;
          
          if (extractedTranscript && extractedTranscript.trim()) {
            setTranscript(extractedTranscript);
          } else {
            console.log('Transcript JSON exists but no transcript text found');
            setTranscript('No transcription available.');
          }
        } catch (parseErr) {
          console.log('Failed to parse transcript as JSON:', parseErr);
          setTranscript('No transcription available.');
        }
      } else {
        console.log("Transcript fetch failed with status:", response.status);
        throw new Error('Transcript not found');
      }
    } catch (err) {
      console.log('No transcript found or error loading transcript:', err);
      setTranscript('No transcription available.');
    }
  };

  // Handle tab change and lazy load content
  const handleTabChange = ({ detail }) => {
    setActiveTab(detail.activeTabId);
    if (video) {
      if (detail.activeTabId === 'recording') {
        loadVideo(video);
      } else if (detail.activeTabId === 'transcript') {
        loadTranscript(video);
      }
    }
  };

  // Load summary from .md file
  const loadSummary = async (videoItem) => {
    try {
      const summaryKey = videoItem.key.replace(/\.webm$/i, '.md');
      console.log("Looking for summary with key:", summaryKey);
      
      const summaryUrl = await getUrl({ 
        path: summaryKey,
        options: { 
          expiresIn: 3600,
          validateObjectExistence: true
        }
      });
      
      const response = await fetch(summaryUrl.url.toString());
      if (response.ok) {
        const summaryText = await response.text();
        setSummary(summaryText);
      } else {
        console.log("Summary fetch failed with status:", response.status);
        throw new Error('Summary not found');
      }
    } catch (error) {
      console.log('No summary found or error loading summary:', error);
      setSummary('No AI summary available for this recording.');
    }
  };

  // Jump to specific time in video
  const jumpToVideoTime = (timeInSeconds) => {
    const videoElement = document.getElementById('meeting-video');
    if (videoElement) {
      videoElement.currentTime = timeInSeconds;
      videoElement.play();
    }
  };

  // Render highlighted transcript
  const renderHighlightedTranscript = () => {
    if (!transcript) return 'No transcript available';
    if (transcriptHighlights.length === 0) return transcript;
    
    let result = [];
    let lastIndex = 0;
    
    transcriptHighlights.forEach((highlight, idx) => {
      // Add text before the highlight
      result.push(
        <span key={`pre-highlight-${idx}`}>
          {transcript.substring(lastIndex, highlight.index)}
        </span>
      );
      
      // Add highlighted text
      result.push(
        <span key={`highlight-${idx}`} style={{ backgroundColor: '#ffff00', fontWeight: 'bold' }}>
          {transcript.substring(highlight.index, highlight.index + highlight.length)}
        </span>
      );
      
      lastIndex = highlight.index + highlight.length;
    });
    
    // Add remaining text
    result.push(
      <span key="post-highlight">
        {transcript.substring(lastIndex)}
      </span>
    );
    
    return result;
  };

  return (
    <Container
      header={
        <Header
          variant="h1"
          actions={
            <Button iconName="arrow-left" onClick={onBack}>
              {backButtonText}
            </Button>
          }
        >
          {video?.displayName}
        </Header>
      }
    >
      <Tabs
        activeTabId={activeTab}
        onChange={handleTabChange}
        tabs={[
          {
            id: 'summary',
            label: 'AI-Summary',
            content: (
              <Box style={{ maxHeight: '400px', overflow: 'auto' }} className="markdown-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{summary}</ReactMarkdown>
              </Box>
            )
          },
          {
            id: 'recording',
            label: 'Recording',
            content: (
              <SpaceBetween size="m">
                <Box>
                  <video 
                    id="meeting-video" 
                    src={videoUrl} 
                    controls 
                    preload="metadata"
                    onLoadedMetadata={(e) => {
                      // Force duration calculation for WebM files
                      if (e.target.duration === Infinity || isNaN(e.target.duration)) {
                        e.target.currentTime = Number.MAX_SAFE_INTEGER;
                        e.target.ontimeupdate = () => {
                          e.target.ontimeupdate = null;
                          e.target.currentTime = 0;
                        };
                      }
                    }}
                    style={{ width: '100%', borderRadius: '4px' }}
                  >
                    Your browser does not support the video tag.
                  </video>
                </Box>
              </SpaceBetween>
            )
          },
          {
            id: 'transcript',
            label: 'Transcript',
            content: (
              <SpaceBetween size="m">
                <FormField label="Search transcript">
                  <Input
                    value={transcriptSearchText}
                    onChange={({ detail }) => setTranscriptSearchText(detail.value)}
                    placeholder="Search for keywords..."
                  />
                </FormField>
                <Box style={{ maxHeight: '400px', overflow: 'auto' }}>
                  <Textarea
                    value={transcript}
                    readOnly
                    rows={15}
                    onChange={() => {}}
                  />
                  <Box padding="s">
                    {transcriptHighlights.length > 0 && (
                      <span>{transcriptHighlights.length} matches found</span>
                    )}
                  </Box>
                </Box>
              </SpaceBetween>
            )
          }
        ]}
      />
    </Container>
  );
};

export default RecordingDetails;
